/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import Toggle from 'src/components/ui/Toggle/Toggle';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import Select from 'src/components/ui/Select/Select';
import { TFunction } from 'i18next';
import { toast } from 'react-toastify';
import styles from './CountriesTable.module.scss';
import Icon from '../../ui/Icon/Icon';
import Modal from '../Modal/Modal';
import { UseFiltersInterface } from '../../../services/filters';
import {
  CountryExt,
  useAllCountriesQuery,
  useAllCountryGroupsQuery,
  useCountryActivate,
  useCountryGroupRemove,
  useCountryGroupUpdate,
  useCountryRemove,
} from '../../../services/countries';
import Image from '../../ui/Image/Image';
import { DataView } from '../../ui/TableOverview/DataView';
import { useDataViewController } from '../../../hooks/useDataViewController';
import { SearchModels } from '../../ui/TableOverview/utils/SearchFieldRegistry';

export interface CountriesTableProps {
  filters: UseFiltersInterface;
  allowEdition?: boolean;
}

const columnHelper = createColumnHelper<any>();
interface ColumnHelperProps {
  t: TFunction;
}

const CountriesTable: React.FC<CountriesTableProps> = ({ filters, allowEdition }) => {
  const { t } = useTranslation();
  const [countryGroupModalOpen, setCountryGroupModalOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryExt | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [rowToggleStates, setRowToggleStates] = useState<{ [key: number]: boolean }>({});

  const [carrierModalOpen, setCarrierModalOpen] = useState(false);

  const { data: countries } = useAllCountriesQuery({
    domain: filters.domain,
  });

  const { data: groups } = useAllCountryGroupsQuery({});
  const updateMutation = useCountryGroupUpdate();
  const removeMutation = useCountryGroupRemove();
  const activateMutation = useCountryActivate();
  const removeCountryMutation = useCountryRemove();

  const getAvailableCountryGroups = useMemo(() => {
    return (groups?.items || []).map((group: any) => ({
      value: group.name,
      label: group.name,
    }));
  }, [groups]);

  const handleModalOpen = useCallback((country: CountryExt, modal: 'country_group' | 'carrier') => {
    if (modal === 'country_group') {
      setCountryGroupModalOpen(true);
      setSelectedCountry(country);
    } else {
      setCarrierModalOpen(true);
      setSelectedCountry(country);
    }
  }, []);

  const handleModalClose = useCallback(() => {
    setCountryGroupModalOpen(false);
    setCarrierModalOpen(false);
    setSelectedCountry(null);
  }, []);

  const saveGroup = useCallback(
    (countryId: number) => {
      const group = groups?.items.find(group => group.name === selectedGroup);
      updateMutation.mutate(
        {
          params: {
            path: {
              country_group_id: Number(group?.id),
              country_id: countryId,
            },
          },
        },
        {
          onSuccess: () => handleModalClose(),
          onError: (e: any) => {
            const detail = e?.detail || e?.message || '';
            const countriesMatch = detail.match(/not supported by the selected carriers:\s*(.+)/i);
            const errorMessage = countriesMatch
              ? t('errors.country_not_supported_error', { countries: countriesMatch[1] })
              : detail || t('errors.add_country_to_group_error');
            toast.error(errorMessage, {
              autoClose: 5000,
            });
          },
        },
      );
    },
    [selectedGroup, groups?.items, handleModalClose, t],
  );

  const activateCountry = useCallback(
    (countryId: number, setActive: boolean) => {
      setRowToggleStates(prev => ({
        ...prev,
        [countryId]: setActive,
      }));

      activateMutation.mutate({
        params: {
          path: {
            country_id: countryId,
            set_active: setActive,
          },
        },
      });
    },
    [activateMutation],
  );

  useEffect(() => {
    if (countries?.items) {
      const initialToggleState = countries.items.reduce(
        (acc, country) => {
          acc[country.id] = country.is_active;
          return acc;
        },
        {} as { [key: number]: boolean },
      );

      setRowToggleStates(initialToggleState);
    }
  }, [countries]);

  const getColumns = ({ t }: ColumnHelperProps) => [
    columnHelper.accessor(row => row.name, {
      id: 'name',
      size: 280,
      enableHiding: true,
      enableSorting: true,
      enableResizing: false,
      meta: {
        label: t('general.country'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex items-center gap-x-20 my-6">
          <Image
            imageUrl={info.row.original?.flag_url || ''}
            alt={info.getValue()}
            className="w-11 h-11 rounded-full"
            fallbackComponent={
              <Icon name="package" className="w-11 h-11 flex items-center justify-center" width={20} height={20} />
            }
          />
          <span className="typo-text-14--bold text-wrap truncate">{info.getValue()}</span>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.continent_name, {
      id: 'continent_name',
      size: 120,
      enableHiding: true,
      enableSorting: true,
      enableResizing: false,
      meta: {
        label: t('general.region'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex flex-col gap-y-4">
          <span className="typo-text-12 text-faded truncate">{info.getValue()}</span>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.country_group?.name, {
      id: 'country_group_ids',
      size: 200,
      enableHiding: true,
      enableSorting: true,
      meta: {
        label: t('general.country_groups'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => {
        const countryGroupName = info.getValue();

        return (
          <button
            type="button"
            className="flex gap-x-4 status-neutral-4-full"
            onClick={allowEdition ? () => handleModalOpen(info.row.original, 'country_group') : () => {}}>
            {countryGroupName && <Icon name="public" width={16} height={16} />}
            <span className="typo-text-12 capitalize">{countryGroupName}</span>
          </button>
        );
      },
    }),
    columnHelper.accessor(row => row.carriers, {
      id: 'carrier_company_ids',
      size: 280,
      enableHiding: true,
      enableSorting: true,
      meta: {
        label: t('general.carrier'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex gap-x-8 gap-y-12 flex-wrap">
          {info.row.original?.carriers?.length > 3 ? (
            <>
              <div className="flex items-center gap-x-8">
                <Image
                  imageUrl={info.getValue()[0]?.logo_url || ''}
                  alt={info.getValue()[0]?.name}
                  className="w-7 h-7 rounded-xs"
                  fallbackComponent={<Icon name="package" width={20} height={20} />}
                />
                <span className="typo-text-14 text-full capitalize">{info.getValue()[0]?.name}</span>
              </div>
              <button
                onClick={() => handleModalOpen(info.row.original, 'carrier')}
                type="button"
                className="typo-text-12 status-neutral-4-full"
                aria-label={t('carrier_analytics.view_all_carriers')}>
                {t('carrier_analytics.more_carriers', { count: info.row.original.carriers.length - 1 })}
              </button>
            </>
          ) : (
            info.getValue().map((carrier: any) => (
              <div key={carrier?.id} className="flex items-center gap-x-8">
                <Image
                  imageUrl={carrier?.logo_url || ''}
                  alt={carrier?.name}
                  className="w-7 h-7 rounded-xs"
                  fallbackComponent={<Icon name="package" width={20} height={20} />}
                />
                <span className="typo-text-14 text-full capitalize">{carrier?.name}</span>
              </div>
            ))
          )}
        </div>
      ),
    }),
    columnHelper.accessor(row => row.is_active, {
      id: 'is_active',
      size: 50,
      enableHiding: true,
      enableSorting: true,
      meta: {
        label: t('general.active'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex flex-col gap-y-4" onClick={event => event.stopPropagation()}>
          <Toggle
            disabled={!allowEdition}
            checked={rowToggleStates[info.row.original.id] ?? info.getValue()}
            onToggle={event => activateCountry(info.row.original.id, event)}
          />
        </div>
      ),
    }),
  ];

  const controller = useDataViewController({
    sorting: [
      {
        id: 'is_active',
        desc: true,
      },
    ],
  });

  return (
    <div className={styles.tableContent}>
      <DataView<CountryExt>
        controller={controller}
        modelKey={SearchModels.Country}
        withSaveFilters={false}
        filterModelId="res.country"
        allowBulkActions={allowEdition}
        displayActionsColumn={allowEdition}
        actions={[
          {
            title: t('carrier_analytics.change_group'),
            icon: 'edit',
            isRowClickAction: allowEdition,
            showInContextMenu: true,
            bulkSupport: false,
            onExecute: selected => {
              if (selected.length > 0) {
                handleModalOpen(selected[0], 'country_group');
              }
            },
          },
          {
            icon: 'close',
            title: t('carrier_analytics.remove_group'),
            titlePlural: t('carrier_analytics.remove_groups'),
            showInContextMenu: true,
            bulkSupport: true,
            isDisabled: selected => {
              return selected.every(country => !country.country_group);
            },
            onExecute: async selected => {
              const ids = selected.map(s => s.id).filter(id => id !== undefined && id !== null);
              await removeMutation.mutateAsync({
                params: {
                  path: {
                    model: 'res.country',
                  },
                },
                body: ids,
              });
            },
            confirmSettings: {
              renderTitle: selected => {
                if (selected.length > 1) {
                  return t('general.remove_groups');
                }
                return t('general.remove_group');
              },
              renderContent: selected => {
                if (selected.length > 1) {
                  return t('general.remove_groups_text');
                }
                return t('general.remove_group_text');
              },
              confirmLabel: t('general.confirm'),
              cancelLabel: t('general.cancel'),
            },
            toast: {
              renderSuccessMessage: () => t('success.country_group_removed'),
              renderErrorMessage: () => t('errors.country_group_remove_failed'),
            },
          },
          {
            icon: 'lock',
            title: t('carrier_analytics.remove_country'),
            showInContextMenu: true,
            bulkSupport: true,
            isDisabled: selected => {
              return selected.every(country => !country.is_active);
            },
            onExecute: async selected => {
              const ids = selected.map(s => s.id).filter(id => id !== undefined && id !== null);
              await removeCountryMutation.mutateAsync({
                params: {
                  path: {
                    model: 'res.country',
                  },
                },
                body: ids,
              });
            },
            // confirmSettings: {
            //   renderTitle: selected => {
            //     if (selected.length > 1) {
            //       return t('general.remove_countries');
            //     }
            //     return t('general.remove_country');
            //   },
            //   renderContent: selected => {
            //     if (selected.length > 1) {
            //       return t('general.remove_countries_text');
            //     }
            //     return t('general.remove_country_text');
            //   },
            //   confirmLabel: t('general.confirm'),
            //   cancelLabel: t('general.cancel'),
            // },
            toast: {
              renderSuccessMessage: () => t('success.country_removed'),
              renderErrorMessage: () => t('errors.country_remove_failed'),
            },
          },
        ]}
        columns={getColumns({ t })}
        useDataQuery={useAllCountriesQuery}
        applyContainer={false}
      />
      <Modal hasBackdrop hasCloseButton isOpen={countryGroupModalOpen} onClose={handleModalClose}>
        <div className={styles.modal}>
          <div className={styles.head}>{t('carrier_analytics.change_group')}</div>
          <div className={classNames(styles.content, 'flex flex-col gap-24')}>
            <Select
              key={null}
              options={getAvailableCountryGroups}
              defaultValue={getAvailableCountryGroups.find(
                group => group.value === selectedCountry?.country_group?.name,
              )}
              placeholder={t('general.select')}
              label={t('carrier_analytics.select_group')}
              className="col-span-1"
              onChange={e => setSelectedGroup(e.value)}
            />
          </div>
          <div className={styles.actions}>
            <Button
              onClick={handleModalClose}
              tone={ButtonToneProps.SECONDARY}
              disabled={false}
              title={t('general.cancel')}
            />
            <Button
              onClick={() => saveGroup(selectedCountry?.id as number)}
              tone={ButtonToneProps.PRIMARY_BLUE}
              title={t('general.apply')}
            />
          </div>
        </div>
      </Modal>
      <Modal hasBackdrop hasCloseButton isOpen={carrierModalOpen} onClose={handleModalClose}>
        <div className={classNames(styles.modal, '!min-w-[320px]')}>
          <div className={styles.head}>{t('general.carrier')}</div>
          <div className={classNames(styles.content, 'flex flex-col gap-12')}>
            {selectedCountry?.carriers?.map((carrier: any) => (
              <div key={carrier.id} className="flex items-center gap-x-8 pb-12 border-subtle-bottom last:border-none">
                <Image
                  imageUrl={carrier.logo_url || ''}
                  alt={carrier.name}
                  className="w-[40px] h-[40px] rounded-sm"
                  fallbackComponent={<Icon name="package" width={20} height={20} />}
                />
                <span className="typo-text-14 text-full capitalize">{carrier?.name}</span>
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CountriesTable;
